import { AdminMainCropDetection } from "services";

import { CROP_TYPE_FCPO, CropFCPO } from "models";

import { COVER_CROP_CHIP, CoverCropChip, formatConfidence } from "pages/admin/verifications/utils/coverCropChips";

type DetectionTone = "success" | "danger" | "info";

const MAIN_CROP_CLASS_LABELS: Record<string, string> = {
  cereal: "Cereal",
  maize: "Maize",
  oilseed: "Oilseed",
  root_crop: "Root crop",
  legume: "Legume",
  grassland_fodder: "Grassland and fodder",
  out_of_scope: "Other crop",
};

export const getMainCropClassLabel = (predictedClass: string | null) =>
  predictedClass ? MAIN_CROP_CLASS_LABELS[predictedClass] ?? predictedClass : "Unknown crop type";

export const getFarmerMainCrop = (cropsOfYear: CropFCPO[]) => cropsOfYear.find(({ cropType, cropId }) => cropType === CROP_TYPE_FCPO.MAIN && cropId);

// Green when the satellite's crop is the farmer's, red when it is another crop. No colour when either side has no crop.
export const getMainCropTone = (detection: AdminMainCropDetection, farmerMainCrop: CropFCPO | undefined): DetectionTone | undefined => {
  const predictedSlug = detection.rankedSuggestions[0]?.cropSlug;
  if (!predictedSlug || !farmerMainCrop) return undefined;

  return predictedSlug === farmerMainCrop.translationSlug ? "success" : "danger";
};

export const getCoverCropResultTag = (chip: CoverCropChip) =>
  chip.type === COVER_CROP_CHIP.NOT_DETECTED
    ? { text: `Not detected · ${formatConfidence(chip.confidence)}`, color: "red" }
    : { text: `Cover crop detected · ${formatConfidence(chip.confidence)}`, color: "blue" };

const COVER_CROP_SUMMARIES: Record<COVER_CROP_CHIP, { tone: DetectionTone; headline: string }> = {
  [COVER_CROP_CHIP.CONFIRMED]: { tone: "success", headline: "Matches platform data" },
  [COVER_CROP_CHIP.NOT_DETECTED]: { tone: "danger", headline: "Farmer reported a cover crop, satellite sees none" },
  [COVER_CROP_CHIP.MISSING]: { tone: "info", headline: "Satellite sees a cover crop the farmer didn't report" },
};

export const getCoverCropSummary = (chip: CoverCropChip) => COVER_CROP_SUMMARIES[chip.type];

// The farmer is only asked when the satellite saw a cover crop and they added nothing but the main crop that year.
export const getFarmerAnswer = (chip: CoverCropChip, cropsOfYear: CropFCPO[]) => {
  const { farmerDecision } = chip.detection;
  if (farmerDecision === "confirmed") return "confirmed there is a cover crop";
  if (farmerDecision === "confirmed_absent") return "said there is no cover crop";

  const wasAsked = chip.type === COVER_CROP_CHIP.MISSING && cropsOfYear.every(({ cropType }) => cropType === CROP_TYPE_FCPO.MAIN);
  return wasAsked ? "hasn't answered yet" : "wasn't asked";
};
